<template>
  <div>
    <BasePageContainer class="record-list-select pt-8 pb-4">
      <PageHeader has-back>
        記録一覧
      </PageHeader>

      <div class="record-list-select__section">
        <div class="record-list-select__section-label">
          対象メンバー
        </div>
        <div class="record-list-select__section-content">
          <MemberFilterSection
            :member-filter.sync="memberFilter"
            :selected-department-ids.sync="selectedDepartmentIds"
            :selected-tag1-values.sync="selectedTag1Values"
            :selected-tag2-values.sync="selectedTag2Values"
            :selected-grades.sync="selectedGrades"
            :selected-homeroom-values.sync="selectedHomeroomValues"
            :department-options="departmentOptions" />
        </div>
      </div>

      <div class="record-list-select__section">
        <div class="record-list-select__section-label">
          対象項目
        </div>
        <div class="record-list-select__section-content">
          <div class="mb-3">
            <b-form-radio
              v-model="selectedItem"
              value="noteRecords">
              ノート記録日数/緊急連絡先/前所属・経歴
            </b-form-radio>
          </div>
          <div class="mb-3">
            <b-form-radio
              v-model="selectedItem"
              value="bodyMeasurements">
              身長体重管理
            </b-form-radio>
            <div
              v-if="selectedItem === 'bodyMeasurements'"
              class="mt-2 ml-4">
              <div class="fs-s font-weight-bold mb-1">
                学年を選択
              </div>
              <div data-testid="body-measurements-grade-select">
                <b-form-select
                  v-model="bodyMeasurementsGradeCode"
                  :options="bodyMeasurementGradeCodeOptions"
                  class="record-list-select__grade-select" />
              </div>
            </div>
          </div>
          <div class="mb-3">
            <b-form-radio
              v-model="selectedItem"
              value="academicRecords">
              テスト成績管理
            </b-form-radio>
            <div
              v-if="selectedItem === 'academicRecords'"
              class="mt-2 ml-4">
              <div class="fs-s font-weight-bold mb-1">
                学年を選択
              </div>
              <div data-testid="academic-records-grade-select">
                <b-form-select
                  v-model="academicRecordsGradeCode"
                  :options="academicRecordGradeCodeOptions"
                  class="record-list-select__grade-select" />
              </div>
            </div>
          </div>
          <div class="mb-3">
            <b-form-radio
              v-model="selectedItem"
              value="pathSurveys">
              進路希望/進路活動記録/指導者間コメント
            </b-form-radio>
          </div>
        </div>
      </div>

      <ButtonsWrapper ref="buttonsWrapper">
        <template #leftTop>
          <BaseButton
            color="cancel"
            @click="$router.go(-1)">
            キャンセル
          </BaseButton>
        </template>
        <template #rightTop>
          <BaseButton @click="onClickList">
            一覧する
          </BaseButton>
        </template>
      </ButtonsWrapper>
    </BasePageContainer>

    <router-view
      v-if="initialized"
      :key="viewKey" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import PageHeader from '@/components/parts/PageHeader.vue'
import ButtonsWrapper from '@/components/parts/ButtonsWrapper.vue'
import BaseButton from '@/components/parts/BaseButton.vue'
import MemberFilterSection from '@/components/view/records/MemberFilterSection.vue'
import { academicRecordGradeCodeOptions, bodyMeasurementGradeCodeOptions } from '@/helper/profile'

const defaultBodyMeasurementsGradeCode = bodyMeasurementGradeCodeOptions.find(o => o.value !== null).value

const STORAGE_KEY = 'recordListSelectForm'

const FORM_FIELDS = [
  'memberFilter',
  'selectedDepartmentIds',
  'selectedTag1Values',
  'selectedTag2Values',
  'selectedGrades',
  'selectedHomeroomValues',
  'selectedItem',
  'bodyMeasurementsGradeCode',
  'academicRecordsGradeCode',
]

export default {
  components: {
    PageHeader,
    ButtonsWrapper,
    BaseButton,
    MemberFilterSection,
  },
  data() {
    return {
      memberFilter: null,
      selectedDepartmentIds: [],
      selectedTag1Values: [],
      selectedTag2Values: [],
      selectedGrades: [],
      selectedHomeroomValues: [],
      selectedItem: null,
      bodyMeasurementsGradeCode: defaultBodyMeasurementsGradeCode,
      academicRecordsGradeCode: academicRecordGradeCodeOptions[0].value,
      initialized: false,
      viewKey: 0,
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    accessibleDepartments() {
      if (!this.user) return []
      return this.user.accessibleDepartments || []
    },
    departmentOptions() {
      return this.accessibleDepartments.map(d => ({ value: d.id, text: d.name }))
    },
    academicRecordGradeCodeOptions() {
      return academicRecordGradeCodeOptions
    },
    bodyMeasurementGradeCodeOptions() {
      return bodyMeasurementGradeCodeOptions
    },
    isAnyItemSelected() {
      return this.selectedItem !== null
    },
    isAnyMemberSelected() {
      if (this.memberFilter === null) return false
      if (this.memberFilter === 'groupTag') {
        return (
          this.selectedDepartmentIds.length > 0 ||
          this.selectedTag1Values.length > 0 ||
          this.selectedTag2Values.length > 0
        )
      }
      if (this.memberFilter === 'gradeHomeroom') {
        return this.selectedGrades.length > 0 || this.selectedHomeroomValues.length > 0
      }
      return false
    },
    routeNameToItemMap() {
      return {
        recordListAcademicRecords: 'academicRecords',
        recordListBodyMeasurements: 'bodyMeasurements',
        recordListNoteRecords: 'noteRecords',
        recordListPathSurveys: 'pathSurveys',
      }
    },
  },
  async created() {
    this.$store.commit('startLoading')
    let redirected = false
    try {
      const tenantInfo = await this.$axios.get('/tenants/').then(res => res.data).catch(() => null)
      if (tenantInfo) {
        this.$store.commit('tenant/setTenantInfo', tenantInfo)
      }
      if (Object.keys(this.$route.query).length > 0) {
        this.restoreFormStateFromQuery()
      } else {
        this.restoreFormState()
        if (!this.academicRecordsGradeCode) {
          this.academicRecordsGradeCode = academicRecordGradeCodeOptions[0].value
        }
        if (!this.bodyMeasurementsGradeCode) {
          this.bodyMeasurementsGradeCode = defaultBodyMeasurementsGradeCode
        }
      }
      this.filterInvalidDepartmentIds()
      if (!this.$store.getters['tenant/studentNumberOption']) {
        this.memberFilter = 'groupTag'
      }
      if (this.$route.name !== 'recordListProfileSelect') {
        if (this.isAnyMemberSelected) {
          this.commitFilteredMembersToStore()
        } else {
          redirected = true
          await this.$router.replace({ name: 'recordListProfileSelect' }).catch(() => {})
        }
      }
    } finally {
      this.$store.commit('finishLoading')
      this.initialized = !redirected
    }
  },
  methods: {
    saveFormState() {
      const state = {}
      FORM_FIELDS.forEach(key => { state[key] = this[key] })
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
      } catch (e) {
        // localStorage が使えない環境では無視する
      }
    },
    restoreFormState() {
      try {
        const saved = localStorage.getItem(STORAGE_KEY)
        if (!saved) return
        const state = JSON.parse(saved)
        FORM_FIELDS.forEach(key => {
          if (key in state) this[key] = state[key]
        })
      } catch (e) {
        // 破損データは無視する
      }
    },
    filterInvalidDepartmentIds() {
      const validIds = new Set(this.departmentOptions.map(o => o.value))
      this.selectedDepartmentIds = this.selectedDepartmentIds.filter(id => validIds.has(id))
    },
    onClickList() {
      if (!this.isAnyItemSelected) {
        this.$store.commit('notify/set', { message: '対象項目を選択してください', type: 'danger' })
        return
      }
      if (!this.isAnyMemberSelected) {
        this.$store.commit('notify/set', { message: '対象メンバーを選択してください', type: 'danger' })
        return
      }
      this.proceedToList()
    },
    async proceedToList() {
      this.commitFilteredMembersToStore()
      this.saveFormState()

      const targetRoute = this.resolveTargetRoute()
      if (!targetRoute) {
        this.$store.commit('notify/set', { message: 'この項目は現在実装中です' })
        return
      }

      const query = this.buildQueryParams()

      if (this.$route.name === targetRoute) {
        await this.$router.replace({ name: targetRoute, query }).catch(() => {})
        this.viewKey++
      } else {
        await this.$router.push({ name: targetRoute, query }).catch(() => {})
      }

      await this.$nextTick()
      if (this.$refs.buttonsWrapper) {
        this.$refs.buttonsWrapper.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    restoreFormStateFromQuery() {
      const q = this.$route.query
      this.selectedItem = this.routeNameToItemMap[this.$route.name] || null
      this.selectedDepartmentIds = q.departments ? q.departments.split(',') : []
      this.selectedTag1Values = q.tag1 ? q.tag1.split(',') : []
      this.selectedTag2Values = q.tag2 ? q.tag2.split(',') : []
      this.selectedGrades = q.grades ? q.grades.split(',') : []
      this.selectedHomeroomValues = q.homerooms ? q.homerooms.split(',') : []
      this.bodyMeasurementsGradeCode = q.body_grade || defaultBodyMeasurementsGradeCode
      this.academicRecordsGradeCode = q.academic_grade || academicRecordGradeCodeOptions[0].value
      if (q.departments || q.tag1 || q.tag2) {
        this.memberFilter = 'groupTag'
      } else if (q.grades || q.homerooms) {
        this.memberFilter = 'gradeHomeroom'
      }
    },
    commitFilteredMembersToStore() {
      this.$store.commit('recordProfile/setAcademicRecordsGradeCode', this.academicRecordsGradeCode)
      this.$store.commit('recordProfile/setFilterParams', this.buildQueryParams())
    },
    resolveTargetRoute() {
      const map = {
        academicRecords: 'recordListAcademicRecords',
        bodyMeasurements: 'recordListBodyMeasurements',
        noteRecords: 'recordListNoteRecords',
        pathSurveys: 'recordListPathSurveys',
      }
      return map[this.selectedItem] || null
    },
    buildQueryParams() {
      const q = {}
      if (this.memberFilter === 'groupTag') {
        if (this.selectedDepartmentIds.length) q.departments = this.selectedDepartmentIds.join(',')
        if (this.selectedTag1Values.length) q.tag1 = this.selectedTag1Values.join(',')
        if (this.selectedTag2Values.length) q.tag2 = this.selectedTag2Values.join(',')
      } else if (this.memberFilter === 'gradeHomeroom') {
        if (this.selectedGrades.length) q.grades = this.selectedGrades.join(',')
        if (this.selectedHomeroomValues.length) q.homerooms = this.selectedHomeroomValues.join(',')
      }
      if (this.selectedItem === 'bodyMeasurements' && this.bodyMeasurementsGradeCode) {
        q.body_grade = this.bodyMeasurementsGradeCode
      }
      if (this.selectedItem === 'academicRecords' && this.academicRecordsGradeCode) {
        q.academic_grade = this.academicRecordsGradeCode
      }
      return q
    },
  },
}
</script>

<style lang="scss" scoped>
.record-list-select {
  &__section {
    display: flex;
    margin-bottom: 24px;
    border: 1px solid $grey11;
    padding: 16px;
    border-radius: 4px;
  }

  &__section-label {
    width: 80px;
    flex-shrink: 0;
    font-weight: bold;
    font-size: $fs-s;
    padding: 4px 8px;
    background-color: $grey03;
    border-radius: 4px;
    height: fit-content;
    white-space: nowrap;
  }

  &__section-label--sub {
    display: block;
    font-size: $fs-xs;
    font-weight: normal;
  }

  &__section-content {
    flex: 1;
    padding-left: 16px;
  }

  &__grade-select {
    width: 200px;
  }
}

@media print {
  .record-list-select {
    display: none;
  }
}
</style>
